/**
 * useAttachmentDisplay Hook (Core)
 *
 * Fetches and converts attachment data for display in messages.
 * Loads attachment metadata from API and formats it for UI components.
 *
 * This hook belongs in core because:
 * - It handles data fetching (business logic)
 * - Makes API calls
 * - Platform-agnostic logic
 * - Part of the conversation/message domain
 */

import { useState, useEffect, useRef } from 'react';
import { apiClient } from '../api/client';
import { ApiUrls } from '../api/ApiUrls';
import {
  loadAttachmentDisplays,
  type AttachedFileDisplay,
  type AttachmentData,
} from '../utils/attachmentDisplay';

export type { AttachedFileDisplay, AttachmentData } from '../utils/attachmentDisplay';

interface UseAttachmentDisplayOptions {
  attachmentIds?: string[];
}

export const useAttachmentDisplay = ({
  attachmentIds = [],
}: UseAttachmentDisplayOptions = {}) => {
  const [attachments, setAttachments] = useState<AttachedFileDisplay[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the last fetched IDs to prevent duplicate fetches
  const lastFetchedIdsRef = useRef<string>('');

  // Create a stable string key for the IDs array
  const idsKey = attachmentIds.join(',');

  // Fetch attachment details from API
  useEffect(() => {
    if (attachmentIds.length === 0) {
      setAttachments([]);
      lastFetchedIdsRef.current = '';
      return;
    }

    // Skip if we already fetched these exact IDs
    if (lastFetchedIdsRef.current === idsKey) {
      return;
    }

    // Mark that we're about to fetch these IDs (prevents React Strict Mode double-mount)
    lastFetchedIdsRef.current = idsKey;

    setIsLoading(true);
    setError(null);

    const fetchAttachments = async () => {
      try {
        // One attachment that fails (deleted with its scope, say) is left out
        // instead of hiding the others.
        const displayAttachments = await loadAttachmentDisplays(attachmentIds, async (id) => {
          const response = await apiClient.get(`${ApiUrls.chatAttachments}${id}`);
          return response.data as AttachmentData;
        });

        setAttachments(displayAttachments);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching attachments:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch attachments');
        setAttachments([]);
        setIsLoading(false);
      }
    };

    fetchAttachments();
  }, [idsKey]); // Only depend on idsKey, not the array itself

  return {
    attachments,
    isLoading,
    error,
  };
};
